import * as React from 'react'
import { parseDate } from '@internationalized/date'
import { useDatePicker } from '@react-aria/datepicker'
import { useDatePickerState } from '@react-stately/datepicker'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'
import { Popover, PopoverContent, PopoverTrigger } from '@wartek-id/popover'

import { DateField } from 'app/KinerjaShared/components/DatePicker/DateField'
import styles from 'app/KinerjaShared/components/DatePicker/DatePicker.module.css'
import { Calendar } from 'app/KinerjaShared/components/DatePicker/Calendar'

import type { DatePickerStateOptions } from '@react-stately/datepicker'
import type { CalendarDate, DateValue } from '@internationalized/date'

export interface DatePickerProps {
  value?: string | null
  minValue?: string
  maxValue?: string
  minYear?: string | number
  maxYear?: string | number
  defaultFocusedValue?: string
  onChange?: (value: string | null) => void
  isDateUnavailable?: (date: DateValue) => boolean
  isDisabled?: boolean
  isInvalid?: boolean
  helperText?: string
  errorMessage?: string
  useCalendar?: boolean
}

const translateDate = (
  stringDate?: string | null
): CalendarDate | undefined => {
  return stringDate ? parseDate(stringDate) : undefined
}

export const DatePicker = React.forwardRef(
  (
    { useCalendar, ...props }: DatePickerProps = {
      isInvalid: false,
      isDisabled: false,
      useCalendar: false,
    },
    ref
  ) => {
    const translatedOnChange = (value: DateValue) => {
      if (props.onChange) {
        props.onChange(value ? value.toString() : null)
      }
    }

    const translatedCalendarProps = {
      value: translateDate(props.value),
      minValue: translateDate(props.minValue),
      maxValue: translateDate(props.maxValue),
      defaultFocusedValue: translateDate(props.defaultFocusedValue),
      onChange: translatedOnChange,
      isDateUnavailable: props.isDateUnavailable,
      label: 'Pilih Tanggal',
    } as DatePickerStateOptions

    const additionalProps = {
      minYear: props.minYear,
      maxYear: props.maxYear,
    }

    const state = useDatePickerState(translatedCalendarProps)

    const { calendarProps: datepickerCalendarProps, fieldProps } =
      useDatePicker(
        translatedCalendarProps,
        state as any,
        ref as React.RefObject<HTMLElement>
      )

    const isInvalid = props.isInvalid || state.validationState === 'invalid'

    const calendarProps = { ...datepickerCalendarProps, ...additionalProps }

    return (
      <>
        <div
          aria-disabled={props.isDisabled}
          data-testid={isInvalid ? 'datepicker-invalid' : 'datepicker-valid'}
          aria-describedby="date-picker-helper"
          className={clsx(
            styles.datePicker,
            props.isDisabled && styles.disabled,
            isInvalid && styles.invalid
          )}
        >
          <DateField {...fieldProps} isDisabled={props.isDisabled} />

          {/* Calendar */}
          {useCalendar && (
            <Popover offset={[20, 15]} onClose={() => state.setOpen(false)}>
              <PopoverTrigger
                as={'button'}
                data-testid="datepicker-trigger"
                className={styles.datePickerCalendarTrigger}
              >
                <Icon color="inherit">date_range</Icon>
              </PopoverTrigger>
              <PopoverContent className={styles.popover}>
                <Calendar
                  data-testid="datepicker-calendar"
                  {...calendarProps}
                />
              </PopoverContent>
            </Popover>
          )}
          {/* End of Calendar */}
        </div>

        {/* Input Helper Text and Error Message */}
        {(isInvalid || props.helperText) && (
          <Text
            variant="helper"
            id="date-picker-helper"
            data-testid="date-picker-helper"
            className={styles.helper}
            color={isInvalid ? 'critical' : 'default'}
          >
            {isInvalid && props.errorMessage}
            {!isInvalid && props.helperText}
          </Text>
        )}
      </>
    )
  }
)

DatePicker.displayName = 'DatePicker'
